import { useRef, useCallback, useEffect, useState, useMemo } from 'react';
import { View } from 'react-native';
import i18n from 'i18n-js';
import { useCurveLinesContextOptional } from '@v2/pages/common/components/NCFileBatchEvents/context/CurveLinesContext';
import { isWeb, parseTimeToSeconds, formatSecondsToTime, Alert, isValidTimeInput } from '@helpers';
import { TimeTileProps } from '@v2/pages/common/components/NCFileBatchEvents/components/TimeTiles/components/TimeTile';
import { OptimisticUpdateCallbacks } from '@v2/pages/common/components/NCFileBatchEvents/types';
import { buildUpdatedStatisticFields } from '@v2/pages/common/components/NCFileBatchEvents/components/TimeTiles/components/TimeTile/helpers/buildUpdatedStatisticFields';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { saveNCFileStatisticsTimes } from '@reducers/statistics';
import { Timestamps } from '@v2/pages/common/components/NCFileBatchEvents/components/TimeTiles/types';
import { ClampingDetails } from '@v2/context/Times/page/interfaces';
import { trackEvent, TRACKING_EVENTS } from '@services/tracking';

export interface UseManageTimeTileParams
  extends Pick<TimeTileProps, 'timeTileConfig' | 'eventType' | 'eventTimelineData' | 'ncFileIdForUpdate' | 'remeasureTrigger'>,
    OptimisticUpdateCallbacks {
  isReadyToShow: boolean;
}

const useManageTimeTile = ({
  timeTileConfig,
  eventType,
  eventTimelineData,
  ncFileIdForUpdate,
  onOptimisticUpdate,
  onOptimisticUpdateError,
  remeasureTrigger,
  isReadyToShow,
}: UseManageTimeTileParams) => {
  const { timeConfig, timeCategory, editable } = timeTileConfig;

  const dispatch = useAppDispatch();

  const dotRef = useRef<View>(null);
  const tileRef = useRef<View>(null);
  const curveLinesContext = useCurveLinesContextOptional();

  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(() => formatSecondsToTime(timeConfig.timeValue ?? 0));
  const [isDirty, setIsDirty] = useState(false);
  const originalValue = useRef(formatSecondsToTime(timeConfig.timeValue ?? 0));

  const parsedInputValue = useMemo(() => parseTimeToSeconds(inputValue), [inputValue]);

  useEffect(() => {
    const formatted = formatSecondsToTime(timeConfig.timeValue ?? 0);
    setInputValue(formatted);
    originalValue.current = formatted;
    setIsDirty(false);
  }, [timeConfig.timeValue]);

  const handleTilePress = useCallback(() => {
    if (editable) {
      setIsEditing(true);
    }
  }, [editable]);

  const handleInputChange = useCallback((text: string) => {
    setInputValue(text);
    setIsDirty(text !== originalValue.current);
  }, []);

  const handleCancel = useCallback(() => {
    setInputValue(originalValue.current);
    setIsDirty(false);
    setIsEditing(false);
  }, []);

  const sendUpdatedTimeToBackend = async (updatedFields: Partial<ClampingDetails>) => {
    if (ncFileIdForUpdate == null) return null;

    return dispatch(saveNCFileStatisticsTimes({ ncFileId: ncFileIdForUpdate, clampingDetails: updatedFields }));
  };

  const handleConfirm = ({ replacedTime, updatedTime }: Timestamps) => {
    // If input is not dirty, just cancel (close without saving)
    if (!isDirty) {
      handleCancel();
      return;
    }

    // Validate time format before proceeding
    if (!isValidTimeInput(inputValue)) {
      Alert({ title: i18n.t('Invalid time format'), text: i18n.t('Please enter time in format h:mm:ss or 1h 30m 45s') });
      return;
    }

    // Build updated fields for both optimistic update and backend call
    const updatedFields = buildUpdatedStatisticFields({
      eventType,
      timeCategory,
      timestamps: { updatedTime, replacedTime },
      eventTimelineData,
    });

    // If no fields to update, skip
    if (!updatedFields) return;

    // Update local input state
    const formatted = formatSecondsToTime(updatedTime);
    setInputValue(formatted);
    originalValue.current = formatted;
    setIsDirty(false);
    setIsEditing(false);

    // 1. Optimistic update - instant UI feedback
    onOptimisticUpdate?.(updatedFields);

    // 2. Backend call with error handling
    const timeCategoryToAction = {
      Ideal: 'idealTimeEdited',
      Paid: 'paidTimeEdited',
      Scheduled: 'scheduledTimeEdited',
    } as const;

    const actionName = timeCategoryToAction[timeCategory as keyof typeof timeCategoryToAction];
    sendUpdatedTimeToBackend(updatedFields).then((response) => {
      if (response?.error) {
        if (actionName) {
          trackEvent(TRACKING_EVENTS.TIMES, {
            action: actionName,
            perspective: 'ncFile' as const,
            subScreen: eventType?.toLowerCase() as 'cycle' | 'active' | 'change' | 'setup',
            ncFileId: String(ncFileIdForUpdate || ''),
            oldTimeSeconds: replacedTime ?? 0,
            newTimeSeconds: updatedTime,
            result: 'failed',
          });
        }
        // On error, refetch to restore correct state
        onOptimisticUpdateError?.();
      } else {
        if (actionName) {
          trackEvent(TRACKING_EVENTS.TIMES, {
            action: actionName,
            perspective: 'ncFile' as const,
            subScreen: eventType?.toLowerCase() as 'cycle' | 'active' | 'change' | 'setup',
            ncFileId: String(ncFileIdForUpdate || ''),
            oldTimeSeconds: replacedTime ?? 0,
            newTimeSeconds: updatedTime,
            result: 'success',
          });
        }
      }
    });
  };

  const registerTileDot = curveLinesContext?.registerTileDot;
  const measurementVersion = curveLinesContext?.measurementVersion ?? 0;

  const measureAndRegisterDot = useCallback(() => {
    if (timeCategory && registerTileDot) {
      if (isWeb) {
        // Web/tablet: use getBoundingClientRect
        const element = dotRef.current as unknown as HTMLElement;
        if (element?.getBoundingClientRect) {
          const rect = element.getBoundingClientRect();
          const position = {
            x: rect.x + rect.width / 2,
            y: rect.y + rect.height / 2,
          };
          registerTileDot(timeCategory, position);
        }
      } else {
        // Native: use measureInWindow
        dotRef.current?.measureInWindow((x, y, width, height) => {
          const position = {
            x: x + width / 2,
            y: y + height / 2,
          };
          registerTileDot(timeCategory, position);
        });
      }
    }
  }, [timeCategory, registerTileDot]);

  // rAF defers measurement to the frame after the React commit so getBoundingClientRect reads the painted layout.
  useEffect(() => {
    if (!isReadyToShow) return;
    const rafId = requestAnimationFrame(() => measureAndRegisterDot());
    return () => cancelAnimationFrame(rafId);
  }, [timeConfig.timeValue, remeasureTrigger, measureAndRegisterDot, isReadyToShow, measurementVersion]);

  return {
    dotRef,
    tileRef,
    isEditing,
    inputValue,
    parsedInputValue,
    originalValue: timeConfig.timeValue ?? 0,
    editable,
    handleTilePress,
    handleInputChange,
    handleCancel,
    handleConfirm,
  };
};

export default useManageTimeTile;
